import { Component, inject, Input, model, ModelSignal, OnInit } from '@angular/core';
import { EvaluationJure } from '../../../../../../../data/commission/models/evaluation-jure.model';
import { JureSynthese } from '../../../../../../../data/commission/models/jure-synthese.model';
import { EvaluationJuresService } from '../../../../../../../data/commission/services/evaluation-jures/evaluation-jures.service';
import { switchMap } from 'rxjs/operators';
import * as moment from 'moment';
import { CriteresEvaluationJure } from '../../../../../../../data/commission/models/criteres-evaluation-jure.model';
import { EvaluationJureCommission } from '../../../../../../../data/commission/models/evaluation-jure-commission.model';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Cahier } from '../../../../../../../data/habilitation/models/cahier.model';
import { LoaderService } from '../../../../../../../core/services/loader/loader.service';
import { Dialog } from 'primeng/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '../../../../../../../shared/shared.module';
import { Message } from 'primeng/message';
import { Divider } from 'primeng/divider';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { MultiSelect } from 'primeng/multiselect';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-detail-evaluation',
    templateUrl: './detail-evaluation.component.html',
    styleUrls: ['./detail-evaluation.component.scss'],
    imports: [Dialog, TranslateModule, ReactiveFormsModule, SharedModule, Message, Divider, DatePicker, FloatLabel, MultiSelect, Button, TableModule],
    standalone: true
})
export class DetailEvaluationComponent implements OnInit {
    /**
     * Signal modal visible
     */
    visible: ModelSignal<boolean> = model(false);

    /**
     * Juré
     */
    @Input() jure: JureSynthese;

    /**
     * L'id organisme
     */
    @Input() idOrganisme: number;

    /**
     * Date début de la période
     */
    @Input() selectedDateDebut: moment.Moment;

    /**
     * Date fin de la période
     */
    @Input() selectedDateFin: moment.Moment;

    /**
     * Cahier des charges
     */
    @Input() selectedCahiers: Cahier[];

    /**
     * Liste des cahiers des charges
     */
    @Input() cahiersList: Cahier[];

    /**
     * Liste des évaluations totales
     */
    evaluations: EvaluationJure[];

    /**
     * Liste des évaluations sur la période
     */
    evaluationsPeriode: EvaluationJure[];

    /**
     * Liste des évaluations avant la période
     */
    evaluationAvantPeriode: EvaluationJure[];

    /**
     * Nombre total de points
     */
    nbTotal = 0;

    /**
     * Nombre de points sur la période
     */
    nbPeriode = 0;

    /**
     * Nombre de points avant la période
     */
    nbAvantPeriode = 0;

    /**
     * Date de début par défaut
     */
    defautDebut: moment.Moment = moment().startOf('year');

    /**
     * Date de fin par défaut
     */
    defautFin: moment.Moment = moment().endOf('year');

    /**
     * Liste des évaluations sur la période groupée par date de commission
     */
    evaluationsGroupedByCommission: EvaluationJureCommission[];

    /**
     * Liste des critères d'évaluation sur la période
     */
    criteresPeriode: CriteresEvaluationJure[];

    /**
     * Formulaire
     */
    formSearch: UntypedFormGroup;

    /**
     * Has result
     */
    hasResult: boolean = false;

    /**
     * Liste des dates de la période sélectionnée
     */
    rangeDates: Date[] = [];

    /**
     * Getter dates de la période sélectionnée
     */
    get rangeDatesCommission() {
        return this.formSearch.get('rangeDates');
    }

    /**
     * Getter Cahiers des charges commission
     */
    get cahiers() {
        return this.formSearch.get('cahierCommission');
    }

    /**
     * Dependencies injection
     */
    private readonly evaluationJuresService = inject(EvaluationJuresService);
    private readonly loaderService = inject(LoaderService);
    private readonly fb = inject(UntypedFormBuilder);

    /**
     * On init component
     */
    ngOnInit() {
        // Instancier le formulaire de recherche
        this.formSearch = this.fb.group({
            rangeDates: [[this.selectedDateDebut?.toDate() || this.defautDebut?.toDate(), this.selectedDateFin?.toDate() || this.defautFin?.toDate()]],
            cahierCommission: [this.selectedCahiers]
        });

        // Evaluations totales
        this.evaluationJuresService
            .getEvaluationsJure(this.jure?.id)
            .pipe(
                switchMap(evaluations => {
                    this.evaluations = evaluations;
                    this.defineDate(evaluations);
                    const idsCriteres = this.evaluations.map(e => e.idCritereEvaluation);
                    return this.evaluationJuresService.getCriteresEvaluationJuresByIds(this.idOrganisme, idsCriteres);
                }),
                switchMap(criteres => {
                    this.nbTotal = this.calculPoints(this.evaluations, criteres);
                    const dateDebut = this.selectedDateDebut ?? this.defautDebut;
                    const dateFin = this.selectedDateFin ?? this.defautFin;

                    return forkJoin({
                        avantPeriode: this.evaluationJuresService.getEvaluationsJure(this.jure?.id, null, null, null, this.retireJour(dateDebut.clone())),
                        periode: this.evaluationJuresService.getEvaluationsJure(this.jure?.id, null, null, dateDebut, dateFin),
                        grouped: this.evaluationJuresService.getEvaluationsJureByCommission(
                            this.jure?.id,
                            null,
                            null,
                            this.selectedCahiers?.map(c => c.id) ?? null,
                            dateDebut,
                            dateFin
                        )
                    });
                }),
                switchMap(({ avantPeriode, periode, grouped }) => {
                    this.evaluationAvantPeriode = avantPeriode;
                    this.evaluationsPeriode = periode;
                    this.evaluationsGroupedByCommission = grouped;

                    const idsAvant = avantPeriode.map(e => e.idCritereEvaluation);
                    const idsPeriode = periode.map(e => e.idCritereEvaluation);
                    const allIds = [...new Set([...idsAvant, ...idsPeriode])];

                    return this.evaluationJuresService.getCriteresEvaluationJuresByIds(this.idOrganisme, allIds);
                }),
                this.loaderService.operator()
            )
            .subscribe(criteres => {
                this.nbAvantPeriode = this.calculPoints(this.evaluationAvantPeriode, criteres);
                this.nbPeriode = this.calculPoints(this.evaluationsPeriode, criteres);
                this.nbTotal = this.calculPoints(this.evaluations, criteres);
                this.criteresPeriode = criteres;
                this.hasResult = true;
            });
    }

    /**
     * Calcule le nombre de points
     * @param listeEvaluation - Liste des évaluations
     * @param criteres - Liste des critères
     */
    calculPoints(listeEvaluation: EvaluationJure[], criteres: CriteresEvaluationJure[]): number {
        let result = 0;
        if (criteres.length > 0) {
            listeEvaluation.forEach(evaluation => {
                const critere = criteres.find(c => c.id === evaluation.idCritereEvaluation);
                result += evaluation.valeurOui ? critere.pointsOui : critere.pointsNon;
            });
        }
        return result;
    }

    /**
     * Retire un jour
     * @param date - La date à modifier
     */
    retireJour(date: moment.Moment) {
        return date ? date.subtract(1, 'day') : date;
    }

    /**
     * Récupère le label du critère
     * @param idCritere - L'id du critère
     */
    getValueCritere(idCritere: number): string {
        const evaluation = this.evaluationsPeriode?.find(e => e.idCritereEvaluation === idCritere);
        return evaluation.valeurOui ? 'Oui' : 'Non';
    }

    /**
     * Récupère le nombre de points par date de commission
     * @param evaluations - Liste d'évaluations
     */
    getNbPoints(evaluations: EvaluationJure[]): number {
        let result = 0;
        evaluations.forEach(evaluation => {
            const critere = this.criteresPeriode?.find(c => c.id === evaluation.idCritereEvaluation);
            if (critere) {
                result += evaluation.valeurOui ? critere.pointsOui : critere.pointsNon;
            }
        });
        return result;
    }

    /**
     * Recherche les évaluations sur la période
     */
    onSubmit() {
        const dates: Date[] = this.rangeDatesCommission.value ?? [];
        const dateDebut = dates[0] ? moment(dates[0]) : null;
        const dateFin = dates[1] ? moment(dates[1]) : null;

        if (!dateDebut || !dateFin) {
            this.evaluationsGroupedByCommission = [];
            this.evaluationsPeriode = [];
            return;
        }

        this.evaluationJuresService
            .getEvaluationsJure(this.jure?.id, null, null, dateDebut, dateFin)
            .pipe(
                switchMap(periode => {
                    this.evaluationsPeriode = periode;
                    const idsCriteres = this.evaluationsPeriode.map(e => e.idCritereEvaluation);
                    return this.evaluationJuresService.getCriteresEvaluationJuresByIds(this.idOrganisme, idsCriteres);
                })
            )
            .subscribe(criteres => {
                this.criteresPeriode = criteres;
                this.nbPeriode = this.calculPoints(this.evaluationsPeriode, criteres);
            });

        this.evaluationJuresService
            .getEvaluationsJureByCommission(this.jure?.id, null, null, this.cahiers.value, dateDebut, dateFin)
            .subscribe(evaluationsGrouped => {
                this.evaluationsGroupedByCommission = evaluationsGrouped;
            });
    }

    /**
     * Définit les dates de début et de fin pour les évaluations
     * @param evaluations - Liste des évaluations
     */
    defineDate(evaluations: EvaluationJure[]): void {
        const plusAncienneDate = moment.min(evaluations.map(e => e.dateCommission));
        const plusRecenteDate = moment.max(evaluations.map(e => e.dateCommission));
        if (plusAncienneDate.isBefore(this.defautDebut)) {
            this.defautDebut = plusAncienneDate;
        }
        if (plusRecenteDate.isAfter(this.defautFin)) {
            this.defautFin = plusRecenteDate;
        }
        this.rangeDatesCommission.setValue([
            this.selectedDateDebut?.toDate() ?? this.defautDebut?.toDate(),
            this.selectedDateFin?.toDate() ?? this.defautFin?.toDate()
        ]);
    }
}
